import type { RefObject } from 'react';
import { useCallback, useEffect } from 'react';
import { useResizeObserver } from 'usehooks-ts';

export interface UseDynamicCSSVarPxOptions {
  /**
   * CSS variable to publish the element's height to (in px).
   */
  height?: string;
  /**
   * CSS variable to publish the element's width to (in px).
   */
  width?: string;
}

/**
 * Publishes an element's height and/or width (in px) to CSS variables on its
 * parent, refreshing on every resize and removing them on unmount.
 */
export const useDynamicCSSVarPx = (
  ref: RefObject<HTMLElement>,
  { height: heightVar, width: widthVar }: UseDynamicCSSVarPxOptions,
): void => {
  const onResize = useCallback(
    ({ height, width }: { height?: number; width?: number }) => {
      const parent = ref.current?.parentElement;
      if (!parent) return;

      if (heightVar && height !== undefined) {
        parent.style.setProperty(heightVar, `${height}px`);
      }

      if (widthVar && width !== undefined) {
        parent.style.setProperty(widthVar, `${width}px`);
      }
    },
    [heightVar, ref, widthVar],
  );

  useResizeObserver({ onResize, ref });

  useEffect(
    () => () => {
      const parent = ref.current?.parentElement;
      if (!parent) return;

      if (heightVar) parent.style.removeProperty(heightVar);
      if (widthVar) parent.style.removeProperty(widthVar);
    },
    [heightVar, ref, widthVar],
  );
};

export default useDynamicCSSVarPx;
